import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Fade, FormControl, Grid, Typography } from '@material-ui/core'
import ErrorIcon from '@material-ui/icons/Error'
import CMSText, { prismicText } from '../Global/CMSText'
import TextField from '../TextField/TextField'
import Button from '../Button/Button'
import useTakeBackStyle from '../../components/TakeBack/TakeBack.style'
import ReferenceModal from './ReferenceModal'
import StatesModal from './StatesModal'
import ImgKitHandler from '../ImgKitHandler/ImgKitHandler'
import { scrollToDiv, thumbnailRatio } from '../../utils/utils'
import { PrismicTakeBack } from '@/interfaces/common/prismicTakeBack'
import AutoCompleteTakeBack from './AutoCompleteTakeBack'
import Loader from '../Loader/Loader'
import { ConfirmationDialog } from '@/components/Dialog'

type Tprops = {
  prismicTakeBack: PrismicTakeBack
  t: any
  onSubmitFirstStep: any
  errorReference: boolean
  disabledButtonsecondPart: boolean
  referenceNumberValue: string
  handleReferenceNumberValue: any
  selectValue: string
  handleSelect: any
  productState: string
  handleState: any
  showCriterion: boolean
  handleCheckReference: any
  handleCheckProduct: (productId: number) => void
  takeBack: any
  entries: any
  main: any
  lang?: string
  currentProductIndex: any
}

type ModalContentType =
  | 'perfectCondition'
  | 'veryGoodCondition'
  | 'goodCondition'
  | null

const TakeBackFirstStepSecondPart = ({
  prismicTakeBack,
  t,
  onSubmitFirstStep,
  errorReference,
  disabledButtonsecondPart,
  referenceNumberValue,
  handleReferenceNumberValue,
  selectValue,
  handleSelect,
  productState,
  handleState,
  showCriterion,
  handleCheckReference,
  handleCheckProduct,
  takeBack,
  entries,
  main,
  lang,
  currentProductIndex,
}: Tprops) => {
  const classes = useTakeBackStyle()
  const takeBackData = prismicTakeBack?.data
  const [openReferenceModal, setOpenReferenceModal] = useState(false)
  const [openStatesModal, setOpenStatesModal] = useState(false)
  const [confirmationDialogState, setConfirmationDialogState] =
    useState<ModalContentType>(null)

  const confirmationModalContent = {
    perfectCondition: takeBackData?.second_confirmation_body_perfect_condition,
    veryGoodCondition:
      takeBackData?.second_confirmation_body_very_good_condition,
    goodCondition: takeBackData?.second_confirmation_body_good_condition,
  }

  const selectSizePlaceholder = prismicText(
    takeBackData?.select_size_placeholder,
    t('texts:takeBack:select')
  )
  const selectStatePlaceholder = prismicText(
    takeBackData?.select_state_placeholder,
    t('texts:takeBack:select')
  )
  const secondHandSize = takeBack?.response?.variants?.filter(
    (variant) => variant.channelPricings.WEB.secondHand
  )
  const mediumImgWidth = main?.data?.medium_image_size
  const aspectRatio = thumbnailRatio(main?.data?.thumbnail_ratio)

  const handleCheckReferenceOnEnter = (event) => {
    if (event === 'Enter') {
      handleCheckReference(referenceNumberValue)
    }
  }

  const submitForm = (event) => {
    event.preventDefault()
  }

  const handleCloseConfirmationDialog = () => {
    setConfirmationDialogState(null)
  }

  const handleStateSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (takeBackData?.show_confirmation) {
      setConfirmationDialogState(event.target.value as ModalContentType)
    }
    handleState(event)
  }

  useEffect(() => {
    showCriterion && scrollToDiv('takeBack__nav')
  }, [showCriterion])

  return (
    <>
      <form
        className={clsx(
          classes.layoutFirstStepSecondPart,
          'takeBack__step',
          'takeBack__firstStep',
          'takeBack__firstStep--second-part',
          !showCriterion && [
            classes.layoutFirstStepSecondPartPending,
            'takeBack__firstStep--pending',
          ],
          showCriterion && 'takeBack__firstStep--success'
        )}
        method="post"
        onSubmit={submitForm}
        id="takeBack__firstStep"
      >
        <Typography className="takeBack-item-title">
          <CMSText
            asText
            data={takeBackData?.item}
            defaultText={t('texts:takeBack:item')}
          />
          {(currentProductIndex += 1)}
        </Typography>
        <div className={clsx(classes.firstPartContainer)}>
          <div className={classes.referenceContainer}>
            <div
              className={clsx(
                'takeBack-searchRef',
                classes.searchRefContainer,
                classes.marginBottom
              )}
            >
              <div
                className={clsx(
                  'takeBack-searchRef__input',
                  classes.searchRefInput,
                  showCriterion && [
                    classes.searchRefInputSuccess,
                    'takeBack-searchRef__input--success',
                  ]
                )}
              >
                <>
                  {takeBackData?.find_product_without_reference ? (
                    <>
                      <AutoCompleteTakeBack
                        label={
                          <CMSText
                            data={takeBackData?.reference_number_title}
                            defaultText={t(
                              'texts:takeBack:reference_number_label'
                            )}
                          />
                        }
                        showCriterion={showCriterion}
                        handleCheckProduct={handleCheckProduct}
                        lang={lang}
                      />
                    </>
                  ) : (
                    <TextField
                      label={
                        <CMSText
                          data={takeBackData?.reference_number_title}
                          defaultText={t(
                            'texts:takeBack:reference_number_label'
                          )}
                        />
                      }
                      name={'referenceNumber'}
                      placeholder={prismicText(
                        takeBackData?.reference_number_placeholder,
                        t('texts:takeBack:reference_number_placeholder')
                      )}
                      value={referenceNumberValue}
                      onChange={handleReferenceNumberValue}
                      onKeyPress={handleCheckReferenceOnEnter}
                      required
                      noMargin
                      isSuccessIcon={showCriterion}
                    />
                  )}
                  <Grid
                    container
                    spacing={1}
                    className={clsx(classes.helpGrid, 'takeBack__help-grid')}
                  >
                    <Grid item xs={6}>
                      <Button
                        variant="text"
                        onClick={() => setOpenReferenceModal(true)}
                        className={clsx(
                          classes.helpLink,
                          'takeBack__help-link'
                        )}
                      >
                        <CMSText
                          asText
                          data={takeBackData?.reference_number_explication_link}
                          defaultText={t(
                            'texts:takeBack:reference_number_link'
                          )}
                        />
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        component="p"
                        className={clsx(
                          classes.firstStepTextRequired,
                          'takeBack__required-field'
                        )}
                        align="right"
                      >
                        <CMSText
                          asText
                          data={prismicTakeBack?.data?.required_field}
                          defaultText={t('texts:takeBack:required_field')}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              </div>
              {errorReference && (
                <Fade in>
                  <Typography
                    variant="body1"
                    component="div"
                    className={clsx(
                      'takeBack__error',
                      'takeBack__error--searchRef',
                      classes.referenceNumberError,
                      classes.error
                    )}
                  >
                    <ErrorIcon color="inherit" fontSize="small" />
                    <div>
                      <CMSText
                        data={takeBackData?.reference_error}
                        defaultText={t('texts:takeBack:reference_error')}
                      />
                    </div>
                  </Typography>
                </Fade>
              )}
              {!takeBackData?.find_product_without_reference && !showCriterion && (
                <div className={classes.searchRefBtn}>
                  <Button
                    size={'small'}
                    variant="contained"
                    color="primary"
                    onClick={() => handleCheckReference(referenceNumberValue)}
                    className={classes.buttonNextPart}
                    disabled={errorReference || referenceNumberValue === ''}
                  >
                    <CMSText
                      asText
                      data={takeBackData?.reference_check_button}
                      defaultText={t('texts:takeBack:reference_check_button')}
                    />
                  </Button>
                </div>
              )}
            </div>

            <ReferenceModal
              open={openReferenceModal}
              handleClose={() => setOpenReferenceModal(false)}
              t={t}
              takeBackData={takeBackData}
            />
            {takeBack?.loading && (
              <div className={classes.loaderContainer}>
                <Loader />
              </div>
            )}
            {!takeBack?.loading && showCriterion && (
              <>
                <div
                  className={clsx(
                    classes.selectContainer,
                    'takeBack__variant__select'
                  )}
                >
                  <FormControl
                    className={clsx(classes.formControl, classes.marginBottom)}
                  >
                    <label
                      className={clsx(
                        classes.label,
                        'takeBack__label',
                        'takeBack__label--select'
                      )}
                    >
                      <CMSText
                        asText
                        data={prismicTakeBack?.data?.select_size_label}
                        defaultText={t('texts:takeBack:select_label_size')}
                      />
                      {'*'}
                    </label>
                    <select
                      value={selectValue}
                      className={clsx(
                        classes.selector,
                        'takeBack__select',
                        'takeBack__select--size'
                      )}
                      onChange={handleSelect}
                    >
                      <option value={''}>{selectSizePlaceholder}</option>
                      {secondHandSize.length > 0 &&
                        secondHandSize.map((variant, index) => {
                          return (
                            <option key={index} value={variant.sku}>
                              {variant?.newSize?.name}
                            </option>
                          )
                        })}
                    </select>
                  </FormControl>
                </div>
                <div
                  className={clsx(
                    classes.selectContainer,
                    'takeBack__state__select'
                  )}
                >
                  <FormControl className={classes.formControl}>
                    <label
                      className={clsx(
                        classes.label,
                        'takeBack__label',
                        'takeBack__label--select'
                      )}
                    >
                      <CMSText
                        asText
                        data={prismicTakeBack?.data?.select_state_label}
                        defaultText={t('texts:takeBack:select_label_state')}
                      />
                      {'*'}
                    </label>
                    <select
                      value={productState}
                      className={clsx(
                        classes.selector,
                        'takeBack__select',
                        'takeBack__select--state'
                      )}
                      onChange={handleStateSelect}
                    >
                      <option value={''}>{selectStatePlaceholder}</option>
                      {entries.map(([key, stateValue]) => {
                        return (
                          <option key={key} value={key}>
                            {stateValue.title}
                          </option>
                        )
                      })}
                    </select>

                    <Grid
                      container
                      spacing={1}
                      className={clsx(classes.helpGrid, 'takeBack__help-grid')}
                    >
                      <Grid item xs={12}>
                        <Button
                          variant="text"
                          onClick={() => setOpenStatesModal(true)}
                          className={clsx(
                            classes.helpLink,
                            'takeBack__help-link'
                          )}
                        >
                          <CMSText
                            asText
                            data={takeBackData?.states_explanation_link}
                            defaultText={t(
                              'texts:takeBack:states_explanation_link'
                            )}
                          />
                        </Button>
                      </Grid>
                    </Grid>
                  </FormControl>
                  <StatesModal
                    open={openStatesModal}
                    handleClose={() => setOpenStatesModal(false)}
                    takeBackData={takeBackData}
                    entries={entries}
                  />
                </div>
              </>
            )}
          </div>

          {!takeBack?.loading && showCriterion && (
            <div className={clsx(classes.referenceImage)}>
              <div data-ratio={aspectRatio}>
                <ImgKitHandler
                  classes={classes.imageSize}
                  imagePaths={takeBack?.response?.images[0]}
                  settings={{
                    width: mediumImgWidth,
                    ar: aspectRatio?.substring(3),
                    defaultImage: main?.data?.default_image,
                  }}
                />
              </div>
              <Typography
                variant={'h3'}
                className={clsx(
                  classes.referenceImageTitle,
                  'takeBack__confirmation_image__title'
                )}
              >
                {takeBack?.response?.name}
              </Typography>
              <div className={classes.center}>
                <Button
                  size={'small'}
                  variant="contained"
                  color="primary"
                  onClick={onSubmitFirstStep}
                  className={classes.buttonNextPart}
                  disabled={disabledButtonsecondPart}
                >
                  <CMSText
                    asText
                    data={takeBackData?.reference_check_button}
                    defaultText={t('texts:takeBack:reference_check_button')}
                  />
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>
      {takeBackData?.show_confirmation && (
        <ConfirmationDialog
          open={!!confirmationDialogState}
          onConfirm={handleCloseConfirmationDialog}
          onClose={handleCloseConfirmationDialog}
          title={takeBackData?.second_confirmation_title}
          body={
            confirmationDialogState &&
            (confirmationModalContent[confirmationDialogState] || null)
          }
          confirmText={takeBackData?.second_confirmation_button_label}
        />
      )}
    </>
  )
}

export default TakeBackFirstStepSecondPart
